import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { rptService } from '../../../services/rptService';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Container,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Refresh,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  Apartment,
  People,
  MonetizationOn,
  Visibility,
  History,
  Description,
  Person,
  Close,
  Event
} from '@mui/icons-material';

const RPTDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Dashboard Stats
  const [stats, setStats] = useState({
    totalProperties: 0,
    totalOwners: 0,
    unpaidAssessments: 0,
    totalCollections: 0,
    currentYear: new Date().getFullYear()
  });
  
  // Recent Data
  const [recentPayments, setRecentPayments] = useState([]);
  const [recentProperties, setRecentProperties] = useState([]);
  const [recentOwners, setRecentOwners] = useState([]);
  
  // Loading States
  const [loading, setLoading] = useState(true);
  
  // Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemType, setItemType] = useState(''); // 'payment', 'property', 'owner'

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load all data in parallel
      const [statsRes, paymentsRes, propertiesRes, ownersRes] = await Promise.all([
        rptService.getDashboardStats(),
        rptService.getRecentPayments(5),
        rptService.getRecentProperties(5),
        rptService.getRecentOwners(5)
      ]);

      if (statsRes.success) setStats(statsRes.data);
      if (paymentsRes.success) setRecentPayments(paymentsRes.data);
      if (propertiesRes.success) setRecentProperties(propertiesRes.data);
      if (ownersRes.success) setRecentOwners(ownersRes.data);
      
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleItemClick = (item, type) => {
    setSelectedItem(item);
    setItemType(type);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    setItemType('');
  };

  const StatCard = ({ title, value, subtext, icon, color, bgcolor }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="overline">
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {loading ? <CircularProgress size={24} /> : value}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {subtext}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: bgcolor, color: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const renderDetailsModal = () => {
    if (!selectedItem) return null;

    let title = '';
    let content = null;
    let icon = null;

    if (itemType === 'payment') {
      title = `Payment Details - OR# ${selectedItem.or_number}`;
      icon = <MonetizationOn color="primary" />;
      content = (
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary">Amount Paid</Typography>
            <Typography variant="h5" color="primary" fontWeight="bold">{formatCurrency(selectedItem.amount_paid)}</Typography>
          </Box>
          <Divider />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Date</Typography>
              <Typography variant="body1">{formatDate(selectedItem.payment_date)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Method</Typography>
              <Typography variant="body1">{selectedItem.payment_method}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Payor</Typography>
              <Typography variant="body1">{selectedItem.payor_name || selectedItem.owner?.owner_name || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Property</Typography>
              <Typography variant="body1">TD: {selectedItem.property?.td_number || 'N/A'}</Typography>
              <Typography variant="body2" color="text.secondary">{selectedItem.property?.barangay}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Period</Typography>
              <Typography variant="body1">{selectedItem.year_from} - {selectedItem.year_to}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Processed By</Typography>
              <Typography variant="body1">{selectedItem.processed_by || 'System'}</Typography>
            </Grid>
          </Grid>
          {selectedItem.remarks && (
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Remarks</Typography>
              <Typography variant="body2">{selectedItem.remarks}</Typography>
            </Box>
          )}
        </Stack>
      );
    } else if (itemType === 'property') {
      title = `Property Details - ${selectedItem.td_number}`;
      icon = <Apartment color="info" />;
      content = (
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary">Assessed Value</Typography>
            <Typography variant="h5" color="info.main" fontWeight="bold">{formatCurrency(selectedItem.assessed_value)}</Typography>
          </Box>
          <Divider />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">PIN</Typography>
              <Typography variant="body1">{selectedItem.pin || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Effectivity</Typography>
              <Typography variant="body1">{selectedItem.effectivity_year}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Owner</Typography>
              <Typography variant="body1">{selectedItem.owner?.owner_name || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Location</Typography>
              <Typography variant="body1">{selectedItem.barangay}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedItem.lot_number ? `Lot ${selectedItem.lot_number} ` : ''}
                {selectedItem.block_number ? `Blk ${selectedItem.block_number}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Class</Typography>
              <Chip label={selectedItem.classification?.classification_name || 'N/A'} size="small" color="primary" variant="outlined" />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Kind</Typography>
              <Chip label={selectedItem.kind?.kind_name || 'N/A'} size="small" color="info" variant="outlined" />
            </Grid>
          </Grid>
        </Stack>
      );
    } else if (itemType === 'owner') {
      title = `Owner Details`;
      icon = <Person color="success" />;
      content = (
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'success.light' }}>
              <Person fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">{selectedItem.owner_name}</Typography>
              <Typography variant="caption" color="text.secondary">Registered Owner</Typography>
            </Box>
          </Box>
          <Divider />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Address</Typography>
              <Typography variant="body1">{selectedItem.address || 'No address recorded'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Contact Number</Typography>
              <Typography variant="body1">{selectedItem.contact_number || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography variant="body1">{selectedItem.email || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Registration Date</Typography>
              <Typography variant="body1">{formatDate(selectedItem.created_at)}</Typography>
            </Grid>
          </Grid>
        </Stack>
      );
    }

    return (
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          {title}
          <IconButton onClick={handleCloseModal} sx={{ ml: 'auto' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {content}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
          {itemType === 'property' && (
            <Button variant="contained" onClick={() => navigate(`/portal/property-tax/properties?search=${selectedItem.td_number}`)}>
              View in Registry
            </Button>
          )}
          {itemType === 'owner' && (
            <Button variant="contained" onClick={() => navigate(`/portal/property-tax/owners?search=${selectedItem.owner_name}`)}>
              View Properties
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ flexGrow: 1, pb: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ pt: 3, pb: 8 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.875rem' }}>
            <Link
              underline="hover"
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => navigate('/portal/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Portal
            </Link>
            <Typography color="text.primary" sx={{ fontWeight: 500 }}>RPT Dashboard</Typography>
          </Breadcrumbs>
        </Box>

        {/* Page Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 800, 
              color: '#1e293b', 
              letterSpacing: '-0.02em',
              background: 'linear-gradient(45deg, #1e293b 30%, #334155 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <DashboardIcon fontSize="large" sx={{ color: '#3b82f6' }} /> Dashboard Overview
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px', lineHeight: 1.6 }}>
              Real-time property tax statistics and recent activities
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
            onClick={loadDashboardData}
            disabled={loading}
            sx={{ 
              bgcolor: '#1e293b', 
              '&:hover': { bgcolor: '#334155' },
              px: 3,
              py: 1.5,
              borderRadius: 1.5,
              fontWeight: 600,
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)'
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Properties"
            value={stats.totalProperties.toLocaleString()}
            subtext="Active property records"
            icon={<Apartment />}
            color="#1976d2"
            bgcolor="#e3f2fd"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Property Owners"
            value={stats.totalOwners.toLocaleString()}
            subtext="Registered owners"
            icon={<People />}
            color="#2e7d32"
            bgcolor="#e8f5e9"
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <StatCard
            title={`Collections ${stats.currentYear}`}
            value={formatCurrency(stats.totalCollections)}
            subtext="Year-to-date revenue"
            icon={<MonetizationOn />}
            color="#9c27b0"
            bgcolor="#f3e5f5"
          />
        </Grid>
      </Grid>


      {/* Recent Activities Section */}
      <Grid container spacing={3}>
        {/* Recent Payments */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <History color="primary" /> Recent Payments
              </Typography>
              <Button size="small" endIcon={<Visibility />} onClick={() => navigate('/portal/property-tax/payments')}>
                View All
              </Button>
            </Box>
            <List disablePadding>
              {loading ? (
                <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>
              ) : recentPayments.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>No recent payments</Box>
              ) : (
                recentPayments.map((payment, index) => (
                  <React.Fragment key={payment.payment_id}>
                    <ListItem 
                      alignItems="flex-start" 
                      button 
                      onClick={() => handleItemClick(payment, 'payment')}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                          <MonetizationOn />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={payment.or_number}
                        secondary={
                          <React.Fragment>
                            <Typography component="span" variant="body2" color="text.primary" fontWeight="bold">
                              {formatCurrency(payment.amount_paid)}
                            </Typography>
                            <br />
                            <Typography component="span" variant="caption" color="text.secondary">
                              {formatDate(payment.payment_date)} • {payment.payor_name || 'N/A'}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    {index < recentPayments.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))
              )}
            </List>
          </Card>
        </Grid>

        {/* Recent Properties */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Apartment color="info" /> Recent Properties
              </Typography>
              <Button size="small" endIcon={<Visibility />} onClick={() => navigate('/portal/property-tax/properties')}>
                View All
              </Button>
            </Box>
            <List disablePadding>
              {loading ? (
                <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>
              ) : recentProperties.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>No recent properties</Box>
              ) : (
                recentProperties.map((prop, index) => (
                  <React.Fragment key={prop.property_id}>
                    <ListItem 
                      alignItems="flex-start" 
                      button 
                      onClick={() => handleItemClick(prop, 'property')}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
                          <Description />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={prop.td_number}
                        secondary={
                          <React.Fragment>
                            <Typography component="span" variant="body2" color="text.primary">
                              {prop.barangay}
                            </Typography>
                            <br />
                            <Typography component="span" variant="caption" color="text.secondary">
                              {prop.owner?.owner_name || 'No Owner'}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    {index < recentProperties.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))
              )}
            </List>
          </Card>
        </Grid>

        {/* Recent Owners */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <People color="success" /> Recent Owners
              </Typography>
              <Button size="small" endIcon={<Visibility />} onClick={() => navigate('/portal/property-tax/owners')}>
                View All
              </Button>
            </Box>
            <List disablePadding>
              {loading ? (
                <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>
              ) : recentOwners.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>No recent owners</Box>
              ) : (
                recentOwners.map((owner, index) => (
                  <React.Fragment key={owner.owner_id}>
                    <ListItem 
                      alignItems="flex-start" 
                      button
                      onClick={() => handleItemClick(owner, 'owner')}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                          <Person />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={owner.owner_name}
                        secondary={
                          <Typography component="span" variant="body2" color="text.secondary" noWrap>
                            Added: {formatDate(owner.created_at)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < recentOwners.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))
              )}
            </List>
          </Card>
        </Grid>
      </Grid>

      {/* Details Modal */}
      {renderDetailsModal()}

        {/* Footer */}
        <Box component="footer" sx={{ py: 6, textAlign: 'center', borderTop: '1px solid #e2e8f0', mt: 8 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Real Property Tax System • Official Government Portal
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            Authorized Personnel Only • Confidential Information
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default RPTDashboard;
